import { Injectable } from '@angular/core';
import * as cutBankInActions from './cutbank-in-db.actions';
import * as cutBankInSelectors from './cutbank-in-db.selectors';

import { select, Store } from '@ngrx/store';

import { AppNotificationUtilClass, InputAppDataService } from '@monorepo/services';
import { AppMasterDBFacade } from '../app-master-db/app-master-db.facade';
import { TInputAppState } from '../reducer';
import {
  TREQUEST_BULK_EDIT_COMPONENT_TAG_QTY,
  TREQUEST_BULK_SAVE_CUTBANK_IN,
  TREQUEST_BUNDLE_TAG_DETAILS,
  TREQUEST_COMPONENT_TAG_VALDATION_CUTBANK_IN,
  TREQUEST_CUTBANK_IN_ACTIVITY_LOG,
  TREQUEST_CUTBANK_IN_TRANSACTION_LOG,
  TREQUEST_DELETE_TRANSACTION_LOG,
  TREQUEST_GET_COMPONENT_BUNDLE_TAG,
  TREQUEST_GET_COMPONENT_TAG_BY_RFID,
  TREQUEST_GET_CUT_TAG,
  TREQUEST_TAG_VALIDATION_CUTBANK_IN,
  TREQUEST_UNDO_TAG_VALIDATION_CUTBANK_IN,
} from '@monorepo/types';
import { finalize, map, switchMap, take } from 'rxjs/operators';
import { from, lastValueFrom } from 'rxjs';
import { InputAppAPIHelperUtil } from '@monorepo/utils';
import { TranslateService } from '@ngx-translate/core';

export const TRANSACTION_LOG_LISTING_ITEMS_PER_PAGE_LIST = [8, 10, 50, 100, 200];
export const ACTIVITY_LOG_LISTING_ITEMS_PER_PAGE_LIST = [8, 10, 50, 100, 200];

@Injectable({ providedIn: 'root' })
export class CutbankInFacade {
  constructor(
    private inputAppDataService: InputAppDataService,
    private appMasterDBFacade: AppMasterDBFacade,
    private store: Store<TInputAppState>,
    private appNotificationUtilClass: AppNotificationUtilClass,
    private inputAppAPIHelperUtil: InputAppAPIHelperUtil,
    private translate: TranslateService,
  ) {}

  fetchSubjects() {
    this.store.dispatch(new cutBankInActions.GetSubjects());
  }

  getSubjects() {
    return this.store.pipe(select(cutBankInSelectors.getSubjects));
  }

  fetchComponentBundleScanEnabled() {
    this.store.dispatch(new cutBankInActions.IsComponentBundleScanEnabled());
  }

  getComponentScanEnabled() {
    return this.store.pipe(select(cutBankInSelectors.IsComponentBundleScanEnabled));
  }

  getCutTagDetailsById(payload: TREQUEST_GET_CUT_TAG) {
    return from(this.appNotificationUtilClass.presentLoading(this.translate.instant('common.loading'))).pipe(
      take(1),
      switchMap((loaderEl) => {
        return this.inputAppDataService
          .getCutBankInCuttag(payload)
          .pipe(finalize(() => this.appNotificationUtilClass.dismissLoadingV2(loaderEl)));
      }),
    );
  }

  async getComponentTagValidation(payload: TREQUEST_COMPONENT_TAG_VALDATION_CUTBANK_IN) {
    const loading = await this.appNotificationUtilClass.presentLoading(this.translate.instant('common.loading'));

    return lastValueFrom(this.inputAppDataService.validateComponentBundleTagCutbankIn(payload)).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  getComponentBundleTagDetailsById(payload: TREQUEST_GET_COMPONENT_BUNDLE_TAG) {
    return from(this.appNotificationUtilClass.presentLoading(this.translate.instant('common.loading'))).pipe(
      map((loaderEl) => {
        return loaderEl;
      }),
      take(1),
      switchMap((loaderEl) => {
        return this.inputAppDataService
          .getCutbankComponentTag(payload)
          .pipe(finalize(() => this.appNotificationUtilClass.dismissLoadingV2(loaderEl)));
      }),
    );
  }

  getCutTagSearchId(tagId: string) {
    return from(this.appNotificationUtilClass.presentLoading(this.translate.instant('common.loading'))).pipe(
      take(1),
      switchMap((loaderEl) => {
        return this.appMasterDBFacade.getSubjectKey().pipe(
          take(1),
          switchMap((subject_key) => {
            return this.inputAppDataService.getCutBankInCuttag({ subject_key, displayTag: tagId }).pipe(
              map((res) => {
                if ('ERROR' in res) {
                  return '';
                } else {
                  const searchId = (res.find((tagInfoList) => tagInfoList.k === 'search_id')?.v as string) || '';
                  return searchId;
                }
              }),
              finalize(() => {
                this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
              }),
            );
          }),
        );
      }),
    );
  }

  async getCutTagValidation(payload: TREQUEST_TAG_VALIDATION_CUTBANK_IN) {
    const loading = await this.appNotificationUtilClass.presentLoading(this.translate.instant('common.loading'));

    return lastValueFrom(this.inputAppDataService.validateTagCutbankIn(payload)).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  async undoTransactionLog(payload: TREQUEST_UNDO_TAG_VALIDATION_CUTBANK_IN) {
    const loading = await this.appNotificationUtilClass.presentLoading(this.translate.instant('common.loading'));

    return lastValueFrom(this.inputAppDataService.getCutbankTransactionUndoValidation(payload)).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  async deleteTransactionLog(payload: TREQUEST_DELETE_TRANSACTION_LOG) {
    const loading = await this.appNotificationUtilClass.presentLoading(this.translate.instant('common.loading'));

    return lastValueFrom(this.inputAppDataService.deleteCutbankTransaction(payload)).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  fetchTransactionLogInfo(payload: TREQUEST_CUTBANK_IN_TRANSACTION_LOG) {
    return this.store.dispatch(new cutBankInActions.GetTransactionLog(payload));
  }

  fetchActivityLogInfo(payload: TREQUEST_CUTBANK_IN_ACTIVITY_LOG) {
    return this.store.dispatch(new cutBankInActions.GetActivityLog(payload));
  }

  getTransactionLog() {
    return this.store.pipe(select(cutBankInSelectors.getTransactionLogInfo));
  }

  getActivityLog() {
    return this.store.pipe(select(cutBankInSelectors.getActivityLogInfo));
  }

  async bulkSaveTransactions(payload: TREQUEST_BULK_SAVE_CUTBANK_IN) {
    const loading = await this.appNotificationUtilClass.presentLoading(this.translate.instant('common.loading'));

    return lastValueFrom(this.inputAppDataService.bulkSaveCutbankTransaction(payload)).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  async bulkSaveComponentTagTransactions(payload: TREQUEST_BULK_SAVE_CUTBANK_IN) {
    const loading = await this.appNotificationUtilClass.presentLoading(this.translate.instant('common.loading'));

    return lastValueFrom(this.inputAppDataService.bulkSaveCutbankComponentTagTransaction(payload)).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  getBundleTagDetails(payload: TREQUEST_BUNDLE_TAG_DETAILS) {
    return from(this.appNotificationUtilClass.presentLoading(this.translate.instant('common.loading'))).pipe(
      switchMap((loaderEl) => {
        return this.inputAppDataService.getBundleTagDetails(payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
        );
      }),
    );
  }

  bulkEditComponentTagQuantities(payload: TREQUEST_BULK_EDIT_COMPONENT_TAG_QTY) {
    return from(this.appNotificationUtilClass.presentLoading()).pipe(
      switchMap((loaderEl) => {
        return this.inputAppDataService.bulkEditComponentTagQty(payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
        );
      }),
    );
  }

  getTagInfoFromScannedRfidTags(payload: TREQUEST_GET_COMPONENT_TAG_BY_RFID) {
    return from(this.appNotificationUtilClass.presentLoading()).pipe(
      switchMap((loaderEl) => {
        return this.inputAppDataService.getComponentTagByRfid(payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
        );
      }),
    );
  }
}

export type TGetSubjectsInfo = ReturnType<CutbankInFacade['getSubjects']>;
export type TTransactionLogInfo = ReturnType<CutbankInFacade['getTransactionLog']>;
export type TActivityLogInfo = ReturnType<CutbankInFacade['getActivityLog']>;
